import { useState } from "react";

import { useTranslation } from "@/i18n";
import { MSG } from "@ease/shared/i18n/message-keys";

interface Subscription {
  tenantId: string;
  tenantName: string;
  plan: string;
  status: "active" | "trialing" | "past_due" | "canceled" | "suspended";
  mrr: number;
  nextBillDate: string;
}

/** F.5: Billing Management — admin-level subscription management */
export default function AdminBillingPage() {
  const { t } = useTranslation();
  const [subscriptions] = useState<Subscription[]>([]);
  const [search, setSearch] = useState("");
  const stats = [
    { label: t(MSG.admin.billing.stats.totalMrr), value: "€0" },
    { label: t(MSG.admin.billing.stats.activeSubscriptions), value: "0" },
    { label: t(MSG.admin.billing.stats.pastDue), value: "0" },
    { label: t(MSG.admin.billing.stats.trials), value: "0" },
  ];

  const statusLabel = (status: Subscription["status"]) => {
    switch (status) {
      case "active":
        return t(MSG.admin.billing.statuses.active);
      case "trialing":
        return t(MSG.admin.billing.statuses.trialing);
      case "past_due":
        return t(MSG.admin.billing.statuses.pastDue);
      case "canceled":
        return t(MSG.admin.billing.statuses.canceled);
      default:
        return t(MSG.admin.billing.statuses.suspended);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">{t(MSG.admin.billing.title)}</h1>

      <div className="grid gap-4 md:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] p-4">
            <p className="text-xs text-muted-foreground">{stat.label}</p>
            <p className="mt-1 text-xl font-bold text-white">{stat.value}</p>
          </div>
        ))}
      </div>

      <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder={t(MSG.admin.billing.searchPlaceholder)}
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-white placeholder:text-muted-foreground" />

      {subscriptions.length === 0 ? (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-12 text-center">
          <p className="text-muted-foreground">{t(MSG.admin.billing.empty)}</p>
        </div>
      ) : (
        <div className="rounded-md border border-[var(--color-border)]">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-background)]">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.admin.billing.table.tenant)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.admin.billing.table.plan)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.admin.billing.table.status)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.admin.billing.table.mrr)}</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase text-muted-foreground">{t(MSG.admin.billing.table.nextBill)}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {subscriptions.map((sub) => (
                <tr key={sub.tenantId} className="border-b border-[var(--color-border)] hover:bg-[var(--color-surface)]/50">
                  <td className="px-4 py-3 text-sm text-white">{sub.tenantName}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{sub.plan}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${
                      sub.status === "active" ? "bg-[var(--color-success)] text-[var(--color-success)]/70" :
                      sub.status === "trialing" ? "bg-[var(--color-primary)]/90 text-[var(--color-primary)]/50" :
                      sub.status === "past_due" ? "bg-yellow-900 text-yellow-300" :
                      "bg-[var(--color-destructive)] text-[var(--color-destructive)]/70"
                    }`}>{statusLabel(sub.status)}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">€{sub.mrr}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{sub.nextBillDate}</td>
                  <td className="px-4 py-3 text-right">
                    <button className="text-xs text-[var(--color-primary)]/70 hover:underline">{t(MSG.admin.billing.actions.overridePlan)}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
